import React, { useContext, useState } from "react";
import { LanguageContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { Heart, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Donate = () => {
  const { language } = useContext(LanguageContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    amount: "",
    purpose: "",
    dedication_message: "",
    is_recurring: false,
    recurring_frequency: "monthly",
  });

  const content = {
    en: {
      title: "Make a Donation",
      subtitle: "Support the Dharamshala, Bhojanshala, and temple services",
      dharmSip: "Dharm SIP (Recurring Donation)",
      dharmSipDesc:
        "Set up a recurring donation to continuously support our spiritual mission",
      name: "Full Name",
      namePlaceholder: "Enter your name",
      email: "Email Address",
      emailPlaceholder: "Enter your email",
      amount: "Donation Amount (₹)",
      amountPlaceholder: "Enter amount",
      purpose: "Purpose (Optional)",
      purposePlaceholder: "Temple maintenance, Bhojanshala, etc.",
      dedication: "Dedication Message (Optional)",
      dedicationPlaceholder: "In memory of...",
      recurringLabel: "Make this a recurring donation (Dharm SIP)",
      frequency: "Frequency",
      monthly: "Monthly",
      quarterly: "Quarterly",
      yearly: "Yearly",
      donate: "Proceed to Payment",
      processing: "Processing...",
      quickAmounts: "Quick Select Amounts",
      note: "Note: You will be redirected to Razorpay for secure payment processing.",
      taxDisclaimer: "No 80G/tax exemption will be provided for donors.",
    },
    hi: {
      title: "दान करें",
      subtitle: "धर्मशाला, भोजनशाला और मंदिर सेवाओं का समर्थन करें",
      dharmSip: "धर्म SIP (आवर्ती दान)",
      dharmSipDesc:
        "हमारे आध्यात्मिक मिशन का लगातार समर्थन करने के लिए आवर्ती दान सेट अप करें",
      name: "पूरा नाम",
      namePlaceholder: "अपना नाम दर्ज करें",
      email: "ईमेल पता",
      emailPlaceholder: "अपना ईमेल दर्ज करें",
      amount: "दान राशि (₹)",
      amountPlaceholder: "राशि दर्ज करें",
      purpose: "उद्देश्य (वैकल्पिक)",
      purposePlaceholder: "मंदिर रखरखाव, भोजनशाला, आदि",
      dedication: "समर्पण संदेश (वैकल्पिक)",
      dedicationPlaceholder: "की याद में...",
      recurringLabel: "इसे आवर्ती दान बनाएं (धर्म SIP)",
      frequency: "आवृत्ति",
      monthly: "मासिक",
      quarterly: "तिमाही",
      yearly: "वार्षिक",
      donate: "भुगतान पर जाएं",
      processing: "प्रक्रिया जारी है...",
      quickAmounts: "त्वरित चयन राशि",
      note: "नोट: आपको सुरक्षित भुगतान प्रसंस्करण के लिए Razorpay पर रीडायरेक्ट किया जाएगा।",
      taxDisclaimer: "दाताओं के लिए कोई 80G/कर छूट प्रदान नहीं की जाएगी।",
    },
  };

  const t = content[language];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.amount) {
      toast.error(
        language === "en"
          ? "Please fill all required fields"
          : "कृपया सभी आवश्यक फ़ील्ड भरें"
      );
      return;
    }

    if (parseFloat(formData.amount) < 1) {
      toast.error(
        language === "en"
          ? "Amount must be at least ₹1"
          : "राशि कम से कम ₹1 होनी चाहिए"
      );
      return;
    }

    setLoading(true);

    try {
      // Create order
      const response = await axios.post(
        `${API}/donations/create-order`,
        formData
      );
      const { order_id, amount, currency, donation_id, key_id } = response.data;

      // Razorpay options
      const options = {
        key: key_id,
        amount: amount,
        currency: currency,
        name: "Sri Siddhachal Sthulabhadra Dham",
        description: formData.is_recurring
          ? `Dharm SIP - ${formData.recurring_frequency}`
          : "Donation",
        order_id: order_id,
        handler: async function (response) {
          try {
            // Verify payment
            await axios.post(`${API}/donations/verify`, {
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_signature: response.razorpay_signature,
              donation_id: donation_id,
            });

            toast.success(
              language === "en"
                ? "Thank you for your generous donation!"
                : "आपके उदार दान के लिए धन्यवाद!"
            );

            // Reset form
            setFormData({
              name: "",
              email: "",
              amount: "",
              purpose: "",
              dedication_message: "",
              is_recurring: false,
              recurring_frequency: "monthly",
            });
          } catch (error) {
            toast.error(
              language === "en"
                ? "Payment verification failed"
                : "भुगतान सत्यापन विफल"
            );
          }
        },
        prefill: {
          name: formData.name,
          email: formData.email,
        },
        theme: {
          color: "#D97706",
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error(
        language === "en"
          ? "Failed to process donation. Please try again."
          : "दान प्रक्रिया विफल। कृपया पुनः प्रयास करें।"
      );
    } finally {
      setLoading(false);
    }
  };

  const quickAmounts = [500, 1000, 2500, 5000, 10000];

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="hero-section py-20 px-4" data-testid="donate-hero">
        <div className="container mx-auto max-w-5xl text-center">
          <Heart className="w-16 h-16 mx-auto mb-6 text-amber-700" />
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold gradient-text mb-6"
            data-testid="donate-title"
          >
            {t.title}
          </h1>
          <p
            className="text-base md:text-lg text-amber-800"
            data-testid="donate-subtitle"
          >
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Dharm SIP Info */}
      <section className="py-8 px-4 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="container mx-auto max-w-3xl">
          <div
            className="donation-card p-8 rounded-2xl text-center"
            data-testid="dharmsip-info"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-amber-900 mb-3">
              {t.dharmSip}
            </h2>
            <p className="text-sm md:text-base text-amber-800">
              {t.dharmSipDesc}
            </p>
          </div>
        </div>
      </section>

      {/* Donation Form */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-2xl">
          <form
            onSubmit={handleSubmit}
            className="space-y-6"
            data-testid="donation-form"
          >
            {/* Name */}
            <div>
              <Label htmlFor="name">{t.name} *</Label>
              <Input
                id="name"
                data-testid="donor-name-input"
                type="text"
                placeholder={t.namePlaceholder}
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                required
              />
            </div>

            {/* Email */}
            <div>
              <Label htmlFor="email">{t.email} *</Label>
              <Input
                id="email"
                data-testid="donor-email-input"
                type="email"
                placeholder={t.emailPlaceholder}
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                required
              />
            </div>

            {/* Quick Amount Selection */}
            <div>
              <Label>{t.quickAmounts}</Label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-2">
                {quickAmounts.map((amt) => (
                  <Button
                    key={amt}
                    type="button"
                    variant="outline"
                    className="border-amber-600 text-amber-700 hover:bg-amber-50"
                    onClick={() =>
                      setFormData({ ...formData, amount: amt.toString() })
                    }
                    data-testid={`quick-amount-${amt}`}
                  >
                    ₹{amt}
                  </Button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div>
              <Label htmlFor="amount">{t.amount} *</Label>
              <Input
                id="amount"
                data-testid="donation-amount-input"
                type="number"
                placeholder={t.amountPlaceholder}
                value={formData.amount}
                onChange={(e) =>
                  setFormData({ ...formData, amount: e.target.value })
                }
                min="1"
                required
              />
            </div>

            {/* Purpose */}
            <div>
              <Label htmlFor="purpose">{t.purpose}</Label>
              <Input
                id="purpose"
                data-testid="donation-purpose-input"
                type="text"
                placeholder={t.purposePlaceholder}
                value={formData.purpose}
                onChange={(e) =>
                  setFormData({ ...formData, purpose: e.target.value })
                }
              />
            </div>

            {/* Dedication */}
            <div>
              <Label htmlFor="dedication">{t.dedication}</Label>
              <Textarea
                id="dedication"
                data-testid="donation-dedication-input"
                placeholder={t.dedicationPlaceholder}
                value={formData.dedication_message}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    dedication_message: e.target.value,
                  })
                }
                rows={3}
              />
            </div>

            {/* Recurring Donation Toggle */}
            <div className="flex items-center space-x-3 p-4 bg-amber-50 rounded-lg">
              <Switch
                id="recurring"
                data-testid="recurring-toggle"
                checked={formData.is_recurring}
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, is_recurring: checked })
                }
              />
              <Label
                htmlFor="recurring"
                className="cursor-pointer text-sm md:text-base"
              >
                {t.recurringLabel}
              </Label>
            </div>

            {/* Frequency */}
            {formData.is_recurring && (
              <div data-testid="frequency-selector">
                <Label htmlFor="frequency">{t.frequency}</Label>
                <Select
                  value={formData.recurring_frequency}
                  onValueChange={(value) =>
                    setFormData({ ...formData, recurring_frequency: value })
                  }
                >
                  <SelectTrigger data-testid="frequency-dropdown">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly" data-testid="freq-monthly">
                      {t.monthly}
                    </SelectItem>
                    <SelectItem value="quarterly" data-testid="freq-quarterly">
                      {t.quarterly}
                    </SelectItem>
                    <SelectItem value="yearly" data-testid="freq-yearly">
                      {t.yearly}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Note */}
            <div className="space-y-1">
              <p className="text-xs md:text-sm text-amber-700 italic">{t.note}</p>
              <p className="text-xs md:text-sm text-red-600 font-medium italic">{t.taxDisclaimer}</p>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              className="w-full btn-primary text-lg"
              disabled={loading}
              data-testid="donate-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                  {t.processing}
                </>
              ) : (
                t.donate
              )}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Donate;
